import React, { useState, useEffect } from "react";
import api from "../api";
import {
  MapPin, Search, Star, ExternalLink, Plus, Loader2, AlertCircle,
  CheckCircle2, Compass,
} from "lucide-react";

const CATEGORY_EXAMPLES = [
  "pizzeria", "dentista", "parrucchiere", "palestra", "ristorante",
  "veterinario", "officina meccanica", "studio commercialista",
];

export default function GoogleMapsDiscover() {
  const [mode, setMode] = useState("text"); // "text" | "nearby"
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [radiusKm, setRadiusKm] = useState(5);
  const [maxResults, setMaxResults] = useState(20);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [lists, setLists] = useState([]);
  const [chosenListId, setChosenListId] = useState("");
  const [newListName, setNewListName] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/lists").then(r => setLists(r.data || []));
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    setError(null); setResults(null); setSelected({}); setImportResult(null);
    if (!query.trim()) return setError("Inserisci una categoria");
    if (mode === "nearby" && !location.trim()) return setError("Inserisci una località");
    setLoading(true);
    try {
      const payload = mode === "text"
        ? { mode, query: query.trim(), location: location.trim(), max_results: maxResults }
        : { mode, keyword: query.trim(), location: location.trim(),
            radius_m: radiusKm * 1000, max_results: maxResults };
      const { data } = await api.post("/google-places/search", payload);
      setResults(data);
      // Auto-select all NOT already in CRM
      const initSel = {};
      for (const c of data.candidates || []) {
        if (!c.in_crm) initSel[c.place_id] = true;
      }
      setSelected(initSel);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const doImport = async () => {
    if (selectedIds.length === 0) return;
    setImporting(true); setError(null);
    try {
      const payload = { place_ids: selectedIds };
      if (newListName.trim()) payload.new_list_name = newListName.trim();
      else if (chosenListId) payload.list_id = chosenListId;
      const { data } = await api.post("/google-places/import", payload);
      setImportResult(data);
      // Reflect "in_crm" in current candidates
      const updated = { ...results };
      updated.candidates = (updated.candidates || []).map(c =>
        selectedIds.includes(c.place_id) ? { ...c, in_crm: true } : c
      );
      setResults(updated);
      setSelected({});
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setImporting(false); }
  };

  return (
    <div data-testid="google-maps-discover">
      <div className="card mb-3" style={{padding:14,background:"#fff7ed",borderLeft:"4px solid var(--mv-primary)"}}>
        <div className="flex items-start gap-2">
          <MapPin size={18} color="var(--mv-primary)" style={{marginTop:2,flexShrink:0}}/>
          <div className="text-small" style={{lineHeight:1.6}}>
            <strong>Google Places API</strong> — trova attività commerciali con dati ufficiali Google (nome, sito, telefono, indirizzo, rating, n. recensioni, categorie).
            <br/>Costo stimato: ~€0,03/risultato (text search) + ~€0,02/risultato in importazione (Place Details).
          </div>
        </div>
      </div>

      <form onSubmit={run} className="card mb-4" style={{padding:18}}>
        {/* Mode tabs */}
        <div className="flex gap-2 mb-3">
          <button type="button" onClick={()=>setMode("text")}
            data-testid="mode-text"
            className={"btn btn-sm " + (mode === "text" ? "btn-primary" : "btn-ghost")}>
            <Search size={13}/> Ricerca per città
          </button>
          <button type="button" onClick={()=>setMode("nearby")}
            data-testid="mode-nearby"
            className={"btn btn-sm " + (mode === "nearby" ? "btn-primary" : "btn-ghost")}>
            <Compass size={13}/> Ricerca per raggio
          </button>
        </div>

        <div className="grid gap-3" style={{gridTemplateColumns: mode === "nearby" ? "2fr 2fr 1fr" : "2fr 2fr"}}>
          <div>
            <label className="label">Categoria / parola chiave *</label>
            <input className="input" placeholder="es. pizzeria"
              value={query} onChange={e=>setQuery(e.target.value)}
              data-testid="gmaps-query"/>
          </div>
          <div>
            <label className="label">{mode === "text" ? "Città (opzionale ma consigliata)" : "Punto centro *"}</label>
            <input className="input" placeholder={mode === "text" ? "es. Milano" : "es. Milano centro"}
              value={location} onChange={e=>setLocation(e.target.value)}
              data-testid="gmaps-location"/>
          </div>
          {mode === "nearby" && (
            <div>
              <label className="label">Raggio (km)</label>
              <input className="input" type="number" min="1" max="50"
                value={radiusKm} onChange={e=>setRadiusKm(Number(e.target.value))}
                data-testid="gmaps-radius"/>
            </div>
          )}
        </div>
        <div className="flex justify-between items-end gap-3 mt-3" style={{flexWrap:"wrap"}}>
          <div>
            <label className="label">Numero max risultati</label>
            <select className="input" value={maxResults}
              onChange={e=>setMaxResults(Number(e.target.value))}
              style={{width: 180}}>
              <option value={20}>20 (~€0,60)</option>
              <option value={40}>40 (~€1,20)</option>
              <option value={60}>60 (~€2,00) — massimo</option>
            </select>
          </div>
          <button type="submit" disabled={loading || !query.trim()}
            className="btn btn-primary" data-testid="gmaps-search-btn">
            {loading ? <><Loader2 size={14} className="spin"/> Ricerca…</> : <><MapPin size={14}/> Cerca su Google Maps</>}
          </button>
        </div>
        {mode === "text" && (
          <div className="text-small text-muted mt-2">
            Esempi: {CATEGORY_EXAMPLES.map((e, i) => (
              <button key={e} type="button" onClick={()=>setQuery(e)}
                className="link" style={{background:"none",border:"none",cursor:"pointer",padding:"0 4px",color:"var(--mv-primary)"}}>
                {e}{i<CATEGORY_EXAMPLES.length-1?" ·":""}
              </button>
            ))}
          </div>
        )}
      </form>

      {error && (
        <div className="card mb-4" style={{padding:14,borderLeft:"4px solid var(--mv-danger)",background:"#fef1f1"}}>
          <div className="flex gap-2 items-start">
            <AlertCircle size={16} color="var(--mv-danger)"/>
            <div className="text-small" style={{color:"#991b1b"}}><strong>Errore:</strong> {error}</div>
          </div>
        </div>
      )}

      {importResult && (
        <div className="card mb-4" style={{padding:14,background:"#dcfce7",borderLeft:"4px solid var(--mv-success)"}}>
          <div className="flex gap-2 items-start">
            <CheckCircle2 size={18} color="var(--mv-success)"/>
            <div className="text-small">
              <strong>Import completato!</strong> {importResult.new_companies} aziende nuove · {importResult.merged} aggiornate · {importResult.added_to_list} aggiunte alla lista
              {importResult.errors > 0 && <> · {importResult.errors} errori</>}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <>
          <div className="flex justify-between items-center mb-3" style={{flexWrap:"wrap",gap:8}}>
            <div>
              <strong>{results.count}</strong> risultati trovati
              {selectedIds.length > 0 && (
                <span className="text-muted"> · <strong>{selectedIds.length}</strong> selezionati per import</span>
              )}
            </div>
            {selectedIds.length > 0 && (
              <div className="flex gap-2 items-center">
                <input className="input" placeholder="Nome nuova lista (opz.)"
                  value={newListName} onChange={e=>setNewListName(e.target.value)}
                  data-testid="gmaps-new-list" style={{width:240}}/>
                <span className="text-small text-muted">oppure</span>
                <select className="input" value={chosenListId} onChange={e=>setChosenListId(e.target.value)}
                  data-testid="gmaps-existing-list" style={{width:180}}>
                  <option value="">— Lista esistente —</option>
                  {lists.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <button onClick={doImport} disabled={importing}
                  className="btn btn-primary btn-sm" data-testid="gmaps-import-btn">
                  {importing ? <Loader2 size={14} className="spin"/> : <Plus size={14}/>}
                  Importa {selectedIds.length}
                </button>
              </div>
            )}
          </div>

          {results.candidates?.length === 0 ? (
            <div className="card text-center" style={{padding:40}}>
              <div className="text-muted">Nessun risultato. Prova a cambiare categoria o località.</div>
            </div>
          ) : (
            <div className="card" style={{padding:0,overflow:"hidden"}}>
            <table className="w-full" style={{borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{background:"#fafafa",fontSize:11,textTransform:"uppercase",color:"var(--mv-text-muted)"}}>
                  <th style={{padding:"10px 12px",width:30}}>
                    <input type="checkbox"
                      checked={selectedIds.length === (results.candidates || []).filter(c=>!c.in_crm).length && selectedIds.length > 0}
                      onChange={e => {
                        const next = {};
                        if (e.target.checked) {
                          for (const c of results.candidates || []) {
                            if (!c.in_crm) next[c.place_id] = true;
                          }
                        }
                        setSelected(next);
                      }}/>
                  </th>
                  <th style={{padding:"10px 12px",textAlign:"left"}}>Nome</th>
                  <th style={{padding:"10px 12px",textAlign:"left"}}>Indirizzo</th>
                  <th style={{padding:"10px 12px",textAlign:"left"}}>Rating</th>
                  <th style={{padding:"10px 12px",textAlign:"left"}}>Categorie</th>
                  <th style={{padding:"10px 12px",textAlign:"left"}}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {(results.candidates || []).map(c => (
                  <tr key={c.place_id} data-testid={`gmaps-row-${c.place_id}`}
                    style={{borderTop:"1px solid #f0f0f0",
                            opacity: c.in_crm ? 0.55 : 1,
                            background: selected[c.place_id] ? "#fff7ed" : "transparent"}}>
                    <td style={{padding:"10px 12px"}}>
                      <input type="checkbox"
                        disabled={c.in_crm}
                        checked={!!selected[c.place_id]}
                        onChange={e=>setSelected({...selected, [c.place_id]: e.target.checked})}/>
                    </td>
                    <td style={{padding:"10px 12px",fontWeight:600}}>{c.name}</td>
                    <td style={{padding:"10px 12px"}} className="text-small">{c.address || "—"}</td>
                    <td style={{padding:"10px 12px"}}>
                      {c.rating ? (
                        <span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                          <Star size={11} fill="#f59e0b" color="#f59e0b"/>
                          <strong>{c.rating}</strong>
                          <span className="text-muted text-small">({c.user_ratings_total || 0})</span>
                        </span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td style={{padding:"10px 12px"}} className="text-small">
                      {(c.categories || []).slice(0, 2).map(t => (
                        <span key={t} style={{background:"#f4f4f5",padding:"1px 7px",borderRadius:99,fontSize:11,marginRight:3}}>
                          {t.replace(/_/g, " ")}
                        </span>
                      ))}
                    </td>
                    <td style={{padding:"10px 12px"}}>
                      {c.in_crm ? (
                        <span style={{background:"#dbeafe",color:"#1e40af",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600}}>
                          ✓ In CRM
                        </span>
                      ) : (
                        <span style={{background:"#dcfce7",color:"#15803d",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600}}>
                          + Nuovo
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
